<template>
  <div>
    <h2>Inventory Sync</h2>
    <p class="text-muted small mb-4">
      New items are imported automatically every 15 minutes and a full sync runs nightly.
      You can also trigger either one manually below.
    </p>

    <!-- ── Quick Sync ──────────────────────────────────────────────── -->
    <div class="card mb-4">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h6 class="mb-0">Quick Sync</h6>
        <small class="text-muted">Runs automatically every 15 min</small>
      </div>
      <div class="card-body">
        <p class="small text-muted mb-3">Checks eBay for new listings and imports them instantly.</p>
        <item-feed standalone />
      </div>
    </div>

    <!-- ── Full Resync ─────────────────────────────────────────────── -->
    <div class="card mb-4">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h6 class="mb-0">Full Inventory Sync</h6>
        <small class="text-muted">Runs automatically nightly</small>
      </div>
      <div class="card-body">
        <p class="small text-muted mb-3">
          Refreshes every listing — prices, quantities, and photos — and clears items no longer on eBay.
        </p>

        <!-- Resync button -->
        <div class="mb-3">
          <button
            v-if="!resyncQueued && !resyncActive && !throttled"
            type="button"
            class="btn btn-sm btn-primary"
            @click="triggerResync"
          >
            <i class="bi bi-arrow-repeat me-2"></i>Run Full Sync Now
          </button>

          <div v-else-if="throttled && !resyncQueued && !resyncActive" class="text-muted small">
            <i class="bi bi-clock me-1"></i>
            Next manual sync available {{ throttleLabel }}
          </div>
        </div>

        <!-- Queued state -->
        <div v-if="resyncQueued" class="alert alert-secondary d-flex align-items-start gap-2 mb-3">
          <i class="bi bi-hourglass-split mt-1 flex-shrink-0"></i>
          <div>
            <strong>Sync queued</strong>
            <div class="small mt-1">
              The job is starting up and will begin within a few minutes.
              You can leave this page — progress will appear here when it starts.
            </div>
          </div>
        </div>

        <!-- In-progress -->
        <div v-if="resyncActive" class="mb-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <span class="fw-semibold small">
              <loading-spinner small="true" class="me-2" />
              {{ resyncStatus.phaseLabel || 'Running sync...' }}
            </span>
            <span class="small text-muted">{{ resyncStatus.processed }} / {{ resyncStatus.total || '?' }}</span>
          </div>
          <div class="progress mb-2" style="height: 6px;">
            <div class="progress-bar" :style="{ width: progressPct + '%' }"></div>
          </div>
          <div class="d-flex justify-content-between">
            <small class="text-muted">
              {{ resyncStatus.updated }} updated
              <span v-if="resyncStatus.zeroed"> · {{ resyncStatus.zeroed }} zeroed</span>
              <span v-if="resyncStatus.reimagined"> · {{ resyncStatus.reimagined }} re-image queued</span>
              <span v-if="resyncStatus.errors" class="text-warning"> · {{ resyncStatus.errors }} errors</span>
              <span v-if="resyncStatus.dryRun" class="text-warning fw-semibold"> · DRY RUN</span>
            </small>
            <small class="text-muted">Phase {{ resyncStatus.phase }} / 2</small>
          </div>
        </div>

        <!-- Result flash -->
        <div v-if="resyncResult" class="alert mb-0" :class="resyncResult.ok ? 'alert-success' : 'alert-danger'">
          <i :class="resyncResult.ok ? 'bi bi-check-circle' : 'bi bi-exclamation-circle'" class="me-2"></i>
          {{ resyncResult.message }}
        </div>
      </div>
    </div>

    <!-- What gets updated -->
    <div class="card mb-4">
      <div class="card-header">
        <h6 class="mb-0">What gets updated from eBay</h6>
      </div>
      <div class="card-body">
        <ul class="small mb-3">
          <li>SKU, title, description &amp; condition</li>
          <li>Price &amp; available quantity</li>
          <li>Photos — changed images are re-queued for cloud storage</li>
          <li>Dimensions &amp; shipping details</li>
          <li>Items no longer listed on eBay are set to quantity 0</li>
        </ul>
        <p class="small text-muted mb-0">
          <i class="bi bi-shield-check text-success me-1"></i>
          Everything you've entered manually — sourcing costs, categories, locations, and
          non-eBay items — is never overwritten by eBay.
        </p>
      </div>
    </div>

    <!-- Last sync -->
    <div v-if="lastRun" class="card">
      <div class="card-header">
        <h6 class="mb-0">Last Sync</h6>
      </div>
      <div class="card-body">
        <dl class="row small mb-0">
          <dt class="col-4">Completed</dt>
          <dd class="col-8">{{ formatDate(lastRun.completedAt) }}</dd>
          <dt class="col-4">Updated</dt>
          <dd class="col-8">{{ lastRun.updated ?? '—' }} items</dd>
          <dt class="col-4">Zeroed</dt>
          <dd class="col-8">{{ lastRun.zeroed ?? '—' }} items</dd>
          <template v-if="lastRun.reimagined">
            <dt class="col-4">Re-imaged</dt>
            <dd class="col-8">{{ lastRun.reimagined }} items queued</dd>
          </template>
          <template v-if="lastRun.errors">
            <dt class="col-4">Errors</dt>
            <dd class="col-8 text-warning">{{ lastRun.errors }}</dd>
          </template>
          <template v-if="lastRun.dryRun">
            <dt class="col-4">Mode</dt>
            <dd class="col-8 text-warning">Dry run</dd>
          </template>
        </dl>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import LoadingSpinner from '@/app/components/Utils/LoadingSpinner.vue'
import ItemFeed from '@/app/components/inventory/ItemFeed.vue'

const RESYNC_HOURS = 1
const store = useStore()

const resyncStatus = ref(null)
const lastResyncAt = ref(null)
const lastRun = ref(null)
const resyncResult = ref(null)
let pollTimer = null

const resyncQueued = computed(() => resyncStatus.value?.status === 'queued')
const resyncActive = computed(() => resyncStatus.value?.status === 'in_progress')

const throttled = computed(() => {
  if (!lastResyncAt.value) return false
  const expires = new Date(lastResyncAt.value).getTime() + RESYNC_HOURS * 60 * 60 * 1000
  return Date.now() < expires
})

const throttleLabel = computed(() => {
  if (!lastResyncAt.value) return ''
  const expires = new Date(lastResyncAt.value).getTime() + RESYNC_HOURS * 60 * 60 * 1000
  const msLeft = expires - Date.now()
  if (msLeft <= 0) return 'now'
  const h = Math.floor(msLeft / 3600000)
  const m = Math.floor((msLeft % 3600000) / 60000)
  return h > 0 ? `in ${h}h ${m}m` : `in ${m}m`
})

const progressPct = computed(() => {
  const s = resyncStatus.value
  if (!s || !s.total) return 0
  return Math.min(100, Math.round((s.processed / s.total) * 100))
})

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  })
}

async function fetchStatus() {
  try {
    const data = await store.dispatch('eshop/getInventoryResyncStatus')
    resyncStatus.value = data.status
    lastResyncAt.value = data.lastResyncAt

    const s = data.status
    if (s?.status === 'queued' || s?.status === 'in_progress') {
      schedulePoll()
    } else if (s?.status === 'completed' || s?.status === 'failed') {
      stopPoll()
      if (s.status === 'completed') {
        lastRun.value = s
        const dry = s.dryRun ? ' (dry run)' : ''
        resyncResult.value = {
          ok: true,
          message: `Resync complete${dry} — ${s.updated} updated, ${s.zeroed} zeroed`
        }
        if (!s.dryRun) store.dispatch('eshop/getInventory')
      } else {
        resyncResult.value = {ok: false, message: `Resync failed: ${s.error}`}
      }
      setTimeout(() => {
        resyncResult.value = null
      }, 8000)
      // Persist last run summary whenever we see a completed status
      if (s?.status === 'completed') {
        lastRun.value = s
      }
    }
  } catch {
    stopPoll()
  }
}

function schedulePoll() {
  if (pollTimer) return
  pollTimer = setInterval(fetchStatus, 2500)
}

function stopPoll() {
  clearInterval(pollTimer)
  pollTimer = null
}

async function triggerResync() {
  resyncResult.value = null
  try {
    await store.dispatch('eshop/startInventoryResync')
    await fetchStatus()
  } catch (err) {
    if (err.response?.status === 409) {
      await fetchStatus()
    } else if (err.response?.status === 429) {
      lastResyncAt.value = err.response.data.lastResyncAt
    } else {
      resyncResult.value = { ok: false, message: err.response?.data?.error || 'Failed to start resync' }
    }
  }
}

onMounted(fetchStatus)
onUnmounted(stopPoll)
</script>
